<template>
  <div v-if="visible" class="dialog-overlay" @click="closeDialog">
    <div class="dialog-container" @click.stop>
      <div class="dialog-header">
        <div class="header-content">
          <div class="header-icon">
            <span class="icon mdi mdi-message-text-outline"></span>
          </div>
          <div class="header-text">
            <h3>Message Details</h3>
            <p>WhatsApp delivery information</p>
          </div>
        </div>
        <button class="close-btn" @click="closeDialog">
          <span>×</span>
        </button>
      </div>

      <div class="dialog-body">
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner"></div>
          <p>Loading message details...</p>
        </div>

        <div v-else-if="error" class="error-container">
          <div class="error-icon mdi mdi-alert-circle-outline"></div>
          <p>{{ error }}</p>
          <button class="retry-btn" @click="fetchMessageDetails">
            Try Again
          </button>
        </div>

        <div v-else-if="messageDetails" class="message-details">
          <!-- Status Card -->
          <div
            class="status-card"
            :class="getStatusClass(messageDetails.status)"
          >
            <div class="status-icon">
              <span
                v-if="messageDetails.status === 'sent'"
                class="mdi mdi-send-check-outline"
              ></span>
              <span
                v-else-if="messageDetails.status === 'failed'"
                class="mdi mdi-alert-circle-outline"
              ></span>
              <span
                v-else-if="messageDetails.status === 'processing'"
                class="mdi mdi-progress-clock"
              ></span>
              <span
                v-else-if="messageDetails.status === 'queued'"
                class="mdi mdi-clock-outline"
              ></span>
              <span v-else class="mdi mdi-send-outline"></span>
            </div>
            <div class="status-content">
              <h4>{{ getStatusText(messageDetails.status) }}</h4>
              <p>{{ getStatusDescription(messageDetails.status) }}</p>
            </div>
          </div>

          <!-- Message Information Grid -->
          <div class="details-grid">
            <div class="detail-section">
              <h4 class="section-title">Basic Information</h4>
              <div class="detail-item">
                <label>Message ID</label>
                <span class="value">{{ messageDetails.id }}</span>
              </div>
              <div class="detail-item">
                <label>Phone Number</label>
                <span class="value phone">{{ messageDetails.phone }}</span>
              </div>
              <div class="detail-item">
                <label>Report Type</label>
                <span class="value report-type">{{
                  messageDetails.reportType
                }}</span>
              </div>
              <div class="detail-item">
                <label>Patient ID</label>
                <span class="value">{{
                  messageDetails.patientId || "Not assigned"
                }}</span>
              </div>
            </div>

            <div class="detail-section">
              <h4 class="section-title">Timeline</h4>
              <div class="detail-item">
                <label>Created At</label>
                <span class="value">{{
                  formatDateTime(messageDetails.createdAt)
                }}</span>
              </div>
              <div class="detail-item">
                <label>Updated At</label>
                <span class="value">{{
                  formatDateTime(messageDetails.updatedAt)
                }}</span>
              </div>
              <div class="detail-item" v-if="messageDetails.sentAt">
                <label>Sent At</label>
                <span class="value">{{
                  formatDateTime(messageDetails.sentAt)
                }}</span>
              </div>
            </div>

            <div class="detail-section">
              <h4 class="section-title">Delivery Stats</h4>
              <div class="detail-item">
                <label>Retry Count</label>
                <span class="value retry-count">{{
                  messageDetails.retryCount
                }}</span>
              </div>
              <div class="detail-item">
                <label>Tenant ID</label>
                <span class="value">{{ messageDetails.tenantId }}</span>
              </div>
              <div class="detail-item">
                <label>Branch ID</label>
                <span class="value">{{ messageDetails.branchId }}</span>
              </div>
            </div>

            <div class="detail-section" v-if="messageDetails.failureReason">
              <h4 class="section-title">Error Information</h4>
              <div class="error-detail">
                <label>Failure Reason</label>
                <span class="value error-text">{{
                  messageDetails.failureReason
                }}</span>
              </div>
            </div>

            <div class="detail-section" v-if="messageDetails.reportUrl">
              <h4 class="section-title">Report Information</h4>
              <div class="detail-item">
                <label>Report URL</label>
                <a
                  :href="messageDetails.reportUrl"
                  target="_blank"
                  class="value link"
                >
                  View Report
                  <span class="external-icon mdi mdi-open-in-new"></span>
                </a>
              </div>
            </div>

            <div class="detail-section" v-if="messageDetails.message">
              <h4 class="section-title">Message Content</h4>
              <div class="message-content">
                <p class="message-text">{{ messageDetails.message }}</p>
              </div>
            </div>

            <div class="detail-section" v-if="messageDetails.apiResponse">
              <h4 class="section-title">API Response</h4>
              <div class="api-response">
                <div class="detail-item">
                  <label>Success</label>
                  <span
                    class="value"
                    :class="
                      messageDetails.apiResponse.success ? 'success' : 'error'
                    "
                  >
                    {{ messageDetails.apiResponse.success ? "Yes" : "No" }}
                  </span>
                </div>
                <div v-if="messageDetails.apiResponse.data" class="api-data">
                  <label>Response Data</label>
                  <div class="json-display">
                    <pre>{{
                      JSON.stringify(messageDetails.apiResponse.data, null, 2)
                    }}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="dialog-footer">
        <button class="btn-secondary" @click="closeDialog">Close</button>
        <button
          v-if="messageDetails && messageDetails.status === 'failed'"
          class="btn-primary"
          @click="retryMessage"
          :disabled="loading"
        >
          Retry Message
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { whatsappApi } from "../services/whatsappApi";

export default {
  name: "MessageDetailsDialog",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    messageId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      messageDetails: null,
      loading: false,
      error: null
    };
  },
  watch: {
    visible(newVal) {
      if (newVal && this.messageId) {
        this.fetchMessageDetails();
      }
    },
    messageId() {
      if (this.visible && this.messageId) {
        this.fetchMessageDetails();
      }
    }
  },
  methods: {
    async fetchMessageDetails() {
      if (!this.messageId) return;

      try {
        this.loading = true;
        this.error = null;
        const response = await whatsappApi.getMessageById(this.messageId);
        this.messageDetails = response.result;
      } catch (error) {
        console.error("Error fetching message details:", error);
        this.error = "Failed to load message details. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    closeDialog() {
      this.$emit("close");
      this.messageDetails = null;
      this.error = null;
    },
    async retryMessage() {
      try {
        this.loading = true;
        await whatsappApi.retryMessage(this.messageId);
        this.$toasted
          .success("Message retry initiated successfully")
          .goAway(3000);
        this.$emit("retry-success");
        this.closeDialog();
      } catch (error) {
        console.error("Error retrying message:", error);
        this.$toasted
          .error("Failed to retry message. Please try again.")
          .goAway(3000);
      } finally {
        this.loading = false;
      }
    },
    getStatusClass(status) {
      return {
        "status-sent": status === "sent",
        "status-failed": status === "failed",
        "status-processing": status === "processing",
        "status-queued": status === "queued"
      };
    },
    getStatusText(status) {
      const statusMap = {
        sent: "Message Sent Successfully",
        failed: "Message Failed",
        processing: "Processing Message",
        queued: "Message Queued"
      };
      return statusMap[status] || "Unknown Status";
    },
    getStatusDescription(status) {
      const descMap = {
        sent: "The message has been delivered successfully",
        failed: "The message delivery failed",
        processing: "The message is being processed",
        queued: "The message is waiting to be sent"
      };
      return descMap[status] || "Status unknown";
    },
    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true
      });
    }
  }
};
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(14, 31, 46, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  isolation: isolate;
}

.dialog-container {
  background: var(--surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--line);
  box-shadow: var(--shadow-md);
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 10000;
  margin: auto;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  border-bottom: 1px solid var(--line);
  background: var(--surface);
  color: var(--text);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 36px;
  height: 36px;
  background: var(--accent-050);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent-600);
  font-size: 19px;
}

.header-text h3 {
  margin: 0;
  font-size: var(--fs-h2);
  font-weight: 700;
  color: var(--ink-900);
}

.header-text p {
  margin: 3px 0 0 0;
  color: var(--muted);
  font-size: var(--fs-sm);
}

.close-btn {
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 22px;
  color: var(--muted);
  transition: background var(--dur) var(--ease);
}

.close-btn:hover {
  background: var(--accent-050);
  color: var(--accent-600);
}

.dialog-body {
  flex: 1;
  overflow-y: auto;
  padding: 28px 32px;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: var(--muted);
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--line);
  border-top: 3px solid var(--accent-600);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.retry-btn {
  background: var(--grad-accent);
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: 500;
  font-size: var(--fs-body);
  margin-top: 16px;
  transition: background var(--dur) var(--ease);
}

.retry-btn:hover {
  background: var(--grad-accent-hover);
}

.message-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Status cards — these carry functional/clinical meaning, keep semantic color with token mapping */
.status-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-radius: var(--radius-md);
  border: 1px solid var(--line);
}

.status-sent {
  background: rgba(31, 122, 82, 0.07);
  border-color: var(--success);
  color: var(--success);
}

.status-failed {
  background: var(--danger-bg);
  border-color: var(--danger);
  color: var(--danger);
}

.status-processing {
  background: var(--accent-050);
  border-color: var(--accent-500);
  color: var(--accent-700);
}

.status-queued {
  background: rgba(154, 98, 18, 0.07);
  border-color: var(--warning);
  color: var(--warning);
}

.status-icon {
  font-size: 22px;
}

.status-content h4 {
  margin: 0 0 2px 0;
  font-size: var(--fs-body);
  font-weight: 600;
}

.status-content p {
  margin: 0;
  font-size: var(--fs-sm);
  opacity: 0.85;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.detail-section {
  background: var(--surface);
  border-radius: var(--radius-md);
  padding: 18px;
  border: 1px solid var(--line);
}

.section-title {
  margin: 0 0 14px 0;
  font-size: var(--fs-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--muted);
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid var(--line);
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item label {
  font-weight: 500;
  color: var(--muted);
  font-size: var(--fs-sm);
}

.detail-item .value {
  font-weight: 600;
  color: var(--text);
  font-size: var(--fs-sm);
  text-align: right;
  max-width: 200px;
  word-break: break-all;
}

.value.phone {
  color: var(--success);
  font-family: var(--font-sans);
  letter-spacing: 0.02em;
}

.value.report-type {
  background: var(--accent-050);
  color: var(--accent-700);
  padding: 3px 8px;
  border-radius: var(--radius-sm);
  font-size: var(--fs-xs);
}

.value.retry-count {
  background: rgba(154, 98, 18, 0.1);
  color: var(--warning);
  padding: 3px 8px;
  border-radius: var(--radius-sm);
  font-size: var(--fs-xs);
}

.value.success {
  color: var(--success);
}

.value.error {
  color: var(--danger);
}

.value.link {
  color: var(--accent-600);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 4px;
}

.value.link:hover {
  text-decoration: underline;
  color: var(--accent-700);
}

.external-icon {
  font-size: 14px;
}

.error-detail {
  background: var(--danger-bg);
  border: 1px solid var(--danger);
  border-radius: var(--radius-sm);
  padding: 14px;
}

.error-text {
  color: var(--danger) !important;
  font-style: italic;
}

.message-content {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  padding: 14px;
}

.message-text {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--text);
  word-break: break-all;
  line-height: 1.6;
}

.api-response {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  padding: 14px;
}

.api-data {
  margin-top: 14px;
}

.api-data label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--muted);
  font-size: var(--fs-sm);
}

.json-display {
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  padding: 10px 12px;
  overflow-x: auto;
}

.json-display pre {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--text);
  white-space: pre-wrap;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px 28px;
  border-top: 1px solid var(--line);
  background: var(--surface-2);
}

.btn-secondary,
.btn-primary {
  padding: 9px 20px;
  border-radius: var(--radius-md);
  font-weight: 500;
  font-size: var(--fs-body);
  cursor: pointer;
  border: none;
  transition: background var(--dur) var(--ease);
}

.btn-secondary {
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--line);
}

.btn-secondary:hover {
  background: var(--surface-2);
  border-color: var(--line-strong);
}

.btn-primary {
  background: var(--grad-accent);
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: var(--grad-accent-hover);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Prevent body scroll when dialog is open */
body.dialog-open {
  overflow: hidden;
}

@media (max-width: 768px) {
  .dialog-container {
    margin: 10px;
    max-height: 95vh;
  }

  .dialog-header {
    padding: 16px 20px;
  }

  .dialog-body {
    padding: 20px;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .dialog-footer {
    padding: 16px 20px;
    flex-direction: column;
  }

  .btn-secondary,
  .btn-primary {
    width: 100%;
  }
}
</style>
